import { useNavigate } from '@tanstack/react-router';
import { formatInTimeZone } from 'date-fns-tz';
import {
    CartesianGrid,
    ResponsiveContainer,
    Scatter,
    ScatterChart,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { OUTREACH_ANALYTICS_TIMEZONE } from '@apps/web/components/admin/outreach_observability/constants';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import {
    type OutreachRunScatterPoint,
    type OutreachStatus,
} from '@libraries/typescript/api_client';

// Fixed status→color buckets. Identity colors (categorical chart tokens), never
// severity — folding the six outreach statuses into four stable buckets so a
// filled run always reads the same hue regardless of what else is on the chart.
type StatusBucket = {
    readonly key: string;
    readonly label: string;
    readonly color: string;
};

const IN_FLIGHT_STATUSES: ReadonlySet<OutreachStatus> = new Set([
    'pending',
    'paused',
    'in_progress',
]);

const STATUS_BUCKETS: ReadonlyArray<StatusBucket> = [
    { key: 'filled', label: 'Filled', color: 'var(--chart-2)' },
    { key: 'in_flight', label: 'In flight', color: 'var(--chart-1)' },
    { key: 'cancelled', label: 'Cancelled', color: 'var(--chart-3)' },
    { key: 'unfilled', label: 'Unfilled', color: 'var(--chart-4)' },
];

function bucketColor(status: OutreachStatus): string {
    if (status === 'filled') {
        return 'var(--chart-2)';
    }
    if (IN_FLIGHT_STATUSES.has(status)) {
        return 'var(--chart-1)';
    }
    if (status === 'cancelled') {
        return 'var(--chart-3)';
    }
    return 'var(--chart-4)';
}

// Three fixed size steps by covered shift count so the reader can spot
// suggestion-heavy multi-shift outreaches by mark size alone.
function pointRadius(coveredShiftCount: number): number {
    if (coveredShiftCount <= 1) {
        return 4;
    }
    if (coveredShiftCount <= 5) {
        return 6;
    }
    return 9;
}

function formatDuration(durationMs: number): string {
    const seconds = durationMs / 1000;
    if (seconds < 60) {
        return `${Math.round(seconds)}s`;
    }
    return `${(seconds / 60).toFixed(1)}m`;
}

function ScatterTooltip({
    active,
    payload,
}: {
    readonly active?: boolean;
    readonly payload?: Array<{ readonly payload?: OutreachRunScatterPoint }>;
}) {
    const point = payload?.[0]?.payload;
    if (!active || !point || point.duration_ms === null) {
        return null;
    }
    return (
        <div className="bg-background rounded-md border px-3 py-2 text-xs shadow-sm">
            <div className="mb-1 font-medium">{point.org_name}</div>
            <div className="text-muted-foreground flex flex-col gap-0.5">
                <span>Status: {point.outreach_status}</span>
                <span className="tabular-nums">
                    Tokens: {point.total_input_tokens.toLocaleString()} in ·{' '}
                    {point.total_output_tokens.toLocaleString()} out
                </span>
                <span className="tabular-nums">
                    Duration: {formatDuration(point.duration_ms)}
                </span>
                <span className="tabular-nums">
                    Shifts: {point.covered_shift_count} · Suggestions:{' '}
                    {point.suggested_caregiver_count}
                </span>
                <span className="tabular-nums">
                    {formatInTimeZone(
                        new Date(point.created_at),
                        OUTREACH_ANALYTICS_TIMEZONE,
                        'M/d h:mm a',
                    )}{' '}
                    ET
                </span>
            </div>
        </div>
    );
}

function StatusLegend() {
    return (
        <div className="text-muted-foreground flex flex-wrap items-center gap-3 text-xs">
            {STATUS_BUCKETS.map((bucket) => (
                <span key={bucket.key} className="flex items-center gap-1.5">
                    <span
                        className="inline-block h-2 w-2 rounded-full"
                        style={{ backgroundColor: bucket.color }}
                    />
                    {bucket.label}
                </span>
            ))}
            <span className="text-muted-foreground/80">
                Larger dots cover more shifts
            </span>
        </div>
    );
}

export function TokensDurationScatter({
    scatter,
}: {
    readonly scatter: Array<OutreachRunScatterPoint>;
}) {
    const navigate = useNavigate();
    const points = scatter.filter((point) => point.duration_ms !== null);
    return (
        <Card>
            <CardHeader>
                <CardTitle>Tokens vs run duration</CardTitle>
                <CardDescription>
                    Newest 500 runs — output tokens against wall-clock duration
                    to spot token-heavy slow runs (e.g. suggestion-heavy
                    outreaches). Color is outreach status; dot size is covered
                    shift count. Click a point to open its agent trace.
                </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
                <StatusLegend />
                {points.length === 0 ? (
                    <div className="text-muted-foreground flex h-72 items-center justify-center rounded-md border text-sm">
                        No runs with recorded duration to plot.
                    </div>
                ) : (
                    <div className="h-72 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <ScatterChart
                                margin={{ left: 8, right: 16, top: 8 }}
                            >
                                <CartesianGrid
                                    stroke="var(--border)"
                                    strokeDasharray="3 3"
                                />
                                <XAxis
                                    dataKey="total_output_tokens"
                                    type="number"
                                    name="Output tokens"
                                    tick={{ fontSize: 11 }}
                                    tickFormatter={(value: number) =>
                                        value.toLocaleString()
                                    }
                                />
                                <YAxis
                                    dataKey="duration_ms"
                                    type="number"
                                    name="Duration"
                                    tick={{ fontSize: 11 }}
                                    width={44}
                                    tickFormatter={(value: number) =>
                                        formatDuration(value)
                                    }
                                />
                                <RechartsTooltip
                                    content={<ScatterTooltip />}
                                    cursor={{ strokeDasharray: '3 3' }}
                                />
                                <Scatter
                                    data={points}
                                    isAnimationActive={false}
                                    shape={(props: {
                                        readonly cx?: number;
                                        readonly cy?: number;
                                        readonly payload?: OutreachRunScatterPoint;
                                    }) => {
                                        const point = props.payload;
                                        if (
                                            !point ||
                                            props.cx === undefined ||
                                            props.cy === undefined
                                        ) {
                                            return <circle r={0} />;
                                        }
                                        const clickable =
                                            point.agent_conversation_id !==
                                            null;
                                        return (
                                            <circle
                                                cx={props.cx}
                                                cy={props.cy}
                                                r={pointRadius(
                                                    point.covered_shift_count,
                                                )}
                                                fill={bucketColor(
                                                    point.outreach_status,
                                                )}
                                                fillOpacity={0.75}
                                                style={{
                                                    cursor: clickable
                                                        ? 'pointer'
                                                        : 'default',
                                                }}
                                                onClick={() => {
                                                    if (
                                                        point.agent_conversation_id ===
                                                        null
                                                    ) {
                                                        return;
                                                    }
                                                    void navigate({
                                                        to: '/admin/agent-traces/$conversationId',
                                                        params: {
                                                            conversationId:
                                                                point.agent_conversation_id,
                                                        },
                                                    });
                                                }}
                                            />
                                        );
                                    }}
                                />
                            </ScatterChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
